// src/services/aeo/clientReviewAgent.ts
//
// The agents that sit INSIDE the client approval loop. Every one returns a
// draft a person approves; none of them can reach the merchant on its own.
//
//   1. Pack drafter: selected tracker tasks in, a pack of blocks out (a
//      section explaining why, a decision per task, a closing "what happens
//      next"), each item's "why" in the merchant's words.
//   2. Doc importer: an existing Google Doc pasted in, the same blocks out.
//      Approval lines and open questions found in the text become decisions.
//   3. Change interpreter: reads a thread's latest client comment or
//      suggested edit and proposes a revision, or a question when the comment
//      gives nothing to act on.
//
// BE contract (proposed, for Ipul). The model call belongs server-side, next
// to the prompts that already write report copy (aeo_report.py) and page
// drafts (aeo_page_draft.py), so the voice rules live in one place:
//
//   POST /businesses/{id}/packs/draft     { tasks }        → PackDraft
//   POST /businesses/{id}/packs/import    { text }         → PackDraft
//   POST /items/{id}/revise               { threadId }     → AgentRevision
//
// With VITE_ENABLE_OURA_LIVE_API unset the functions simulate the agent
// deterministically, with a delay, so the drafting states are real.

import axios from "axios";
import { getCookie } from "@util/cookie";
import type { PackBlock, PackDraft, PackItem, ReviewItemKind, Thread } from "./clientReview";

const apiURL = import.meta.env.VITE_TALENT_AI_BE_BASE_URL;
const AEO_BASE = `${apiURL}/api/v1`;
// Its own flag, separate from VITE_ENABLE_OURA_LIVE_API: the rest of Oura is
// live on staging while these endpoints are still being built (epic
// ourteam-backend#4735). Off means the in-memory store below. T16 flips it.
const useFixtures = import.meta.env.VITE_ENABLE_OURA_CLIENT_REVIEW_LIVE !== "true";
const enc = encodeURIComponent;

function authHeaders(): Record<string, string> {
    const token = getCookie("authToken");
    return token ? { Authorization: `Bearer ${token}` } : {};
}

export interface AgentTaskInput {
    taskId: string;
    action: string;
    notes: string;
    cause?: string;
    owner: string;
    kind: ReviewItemKind;
    sourceQueries?: string[];
    targetPage?: string;
}

/** A PackDraft plus, per item, one line on how the agent framed it. Staff-only. */
export interface AgentPackDraft extends PackDraft {
    reasoning: Record<string, string>;
}

export interface AgentRevision {
    itemId: string;
    understood: string;
    proposed: string;
    questionForClient: string | null;
    reasoning: string;
}

export async function draftPack(businessId: string, businessName: string, tasks: AgentTaskInput[]): Promise<AgentPackDraft> {
    if (useFixtures) return mock.draftPack(businessName, tasks);
    const res = await axios.post(`${AEO_BASE}/businesses/${enc(businessId)}/packs/draft`, { tasks }, { headers: authHeaders() });
    return res.data.data as AgentPackDraft;
}

export async function importDoc(businessId: string, businessName: string, text: string): Promise<AgentPackDraft> {
    if (useFixtures) return mock.importDoc(businessName, text);
    const res = await axios.post(`${AEO_BASE}/businesses/${enc(businessId)}/packs/import`, { text }, { headers: authHeaders() });
    return res.data.data as AgentPackDraft;
}

export async function reviseItem(item: PackItem, thread: Thread | null): Promise<AgentRevision> {
    if (useFixtures) return mock.reviseItem(item, thread);
    const res = await axios.post(`${AEO_BASE}/items/${enc(item.id)}/revise`, { threadId: thread?.id ?? null }, { headers: authHeaders() });
    return res.data.data as AgentRevision;
}

// ---------------------------------------------------------------------------
// Simulated agent. Replace with the BE call, not with more heuristics.
// ---------------------------------------------------------------------------

let seq = 0;
const bid = (p: string) => `${p}_${++seq}_${Date.now().toString(36)}`;
function delay<T>(v: T, ms: number): Promise<T> { return new Promise((r) => window.setTimeout(() => r(v), ms)); }
const sentence = (s: string) => { const t = s.trim(); return !t ? "" : /[.!?]$/.test(t) ? t : `${t}.`; };

/** Strip the jargon the catalogue bans from merchant-facing copy. */
function plain(s: string): string {
    return s
        .replace(/\bmeta description(s)?\b/gi, "page description$1")
        .replace(/\bH1\b/g, "page heading")
        .replace(/\b(schema|structured data)\b/gi, "business facts AI can read")
        .replace(/\bcrawl(ed|ers?)?\b/gi, "read")
        .replace(/\bengines?\b/gi, "AI and Google")
        .replace(/\bCMS\b/g, "website admin")
        .replace(/\bGSC\b/g, "Search Console")
        .replace(/\bGA4\b/g, "Google Analytics")
        .replace(/\bindex(ed|ing)?\b/gi, "listed on Google");
}

function whyFor(t: AgentTaskInput): string {
    const base = plain(t.notes || t.cause || "");
    const qs = t.sourceQueries?.length ? ` ${t.sourceQueries.length} of your 50 buyer questions ask this, and no page on your site answers it today.` : "";
    switch (t.kind) {
        case "approve": return sentence(base) + qs + " Your customers will see this change, so we want your yes before it goes live.";
        case "choose": return sentence(base) + " Only you know the right answer, and we fix everything downstream once you pick.";
        case "provide_file": return sentence(base) + " Send the file and we handle the rest.";
    }
}
function titleFor(t: AgentTaskInput): string {
    const a = t.action.trim();
    if (t.kind === "approve" && !/^(approve|publish|rewrite|write|add|update|change)\b/i.test(a)) return `Approve: ${a}`;
    if (t.kind === "choose" && !/^(confirm|choose|pick|tell us)\b/i.test(a)) return `Confirm: ${a}`;
    if (t.kind === "provide_file" && !/^(send|upload|provide)\b/i.test(a)) return `Send us: ${a}`;
    return a;
}
function tagsFor(t: AgentTaskInput): string[] {
    const text = `${t.action} ${t.notes}`; const tags: string[] = [];
    if (/\bnew page|write a page|publish a page|pages? (written|published)\b/i.test(text)) tags.push("New page");
    else if (/\b(page copy|site copy|paragraph|headline|title|description|rewrite)\b/i.test(text)) tags.push("Copy change");
    if (/\bsignature\b/i.test(text)) tags.push("Signature page");
    if (/\bgoogle business|profile\b/i.test(text)) tags.push("Google profile");
    return tags;
}
function outlineFor(t: AgentTaskInput): string[] | undefined {
    if (t.kind !== "approve" || !/\b(page|pages|entries|faq)\b/i.test(t.action)) return undefined;
    const qs = t.sourceQueries?.slice(0, 3);
    if (qs?.length) return qs.map((q) => `Answers "${q}" in the first paragraph, then the detail`);
    return ["What it is and who it is for, in the first two sentences", "Price range and what changes it", "How to book, with your outlet, hours and phone on the page"];
}
function optionsFor(t: AgentTaskInput): string[] | undefined {
    if (t.kind !== "choose") return undefined;
    if (/\bhours?\b/i.test(t.action)) return ["9:00am to 6:00pm daily", "9:00am to 8:00pm daily", "Different on weekends"];
    if (/\breview|copy|sign.?off\b/i.test(t.action)) return ["Approve as written", "I want to see it before it goes out", "Skip this month"];
    return undefined;
}

const mock = {
    async draftPack(businessName: string, tasks: AgentTaskInput[]): Promise<AgentPackDraft> {
        const items: PackDraft["items"] = tasks.map((t) => ({
            taskId: t.taskId, kind: t.kind, title: titleFor(t), why: whyFor(t), tags: tagsFor(t),
            outline: outlineFor(t), options: optionsFor(t),
        }));
        const approvals = items.filter((i) => i.kind === "approve").length;
        const answers = items.length - approvals;
        const parts = [approvals > 0 && `${approvals} change${approvals === 1 ? "" : "s"} to approve`, answers > 0 && `${answers} thing${answers === 1 ? "" : "s"} to confirm`].filter(Boolean);
        const blocks: PackBlock[] = [
            { id: bid("b"), type: "section", heading: "Why we are asking", body: `${parts.join(" and ")} from this month's plan. Each is something your customers will see, or a fact only you know. Nothing goes live until you say yes.` },
            ...items.map((i): PackBlock => ({ id: bid("b"), type: "decision", itemId: i.taskId })),
            { id: bid("b"), type: "section", heading: "What happens next", body: "You answer each item, or ask a question under it. We start the approved work the same day and record every change on your Progress page, with the proof." },
        ];
        const reasoning: Record<string, string> = Object.fromEntries(items.map((i) => [i.taskId,
            i.kind === "approve" ? "Customer-visible change, so framed as a yes or no with the reason first."
                : i.kind === "choose" ? "Needs a fact only the merchant has, so framed as a pick, not a comment box."
                    : "Needs a file, so framed as an upload with why it matters."]));
        return delay({ title: `Decisions for ${businessName}`, intro: `${parts.join(" and ")}. Work starts on each the moment you answer.`, blocks, items, reasoning }, 900 + tasks.length * 200);
    },

    /**
     * Splits a pasted Doc on headings, lifts "☐" and "Please approve" lines
     * out as decisions, keeps everything else as prose. A simulation of the
     * server-side model, good enough to move a real pack across.
     */
    async importDoc(businessName: string, text: string): Promise<AgentPackDraft> {
        const lines = text.replace(/\r/g, "").split("\n");
        const blocks: PackBlock[] = []; const items: PackDraft["items"] = []; const reasoning: Record<string, string> = {};
        let heading = ""; let buf: string[] = []; let inApprove = false; let n = 0;
        const flush = () => {
            const body = buf.join("\n").trim(); buf = [];
            if (!body && !heading) return;
            if (body) blocks.push({ id: bid("b"), type: "section", heading: heading || "Overview", body });
        };
        for (const raw of lines) {
            const line = raw.trim();
            if (!line) { if (buf.length) buf.push(""); continue; }
            const h = line.match(/^#{1,3}\s+(.*)$/);
            if (h) { flush(); heading = h[1].trim(); inApprove = /please approve|need .* from you|quick things/i.test(heading); continue; }
            const tick = line.match(/^(?:☐|\[ \]|-\s*\[ \])\s*(.+)$/);
            if (tick || (inApprove && /^[A-Z][^.]{8,}:\s/.test(line))) {
                const body = (tick ? tick[1] : line).trim();
                const [head, ...rest] = body.split(/:\s+/);
                const isQuestion = /\?/.test(body) || /\b(which|does|do you|confirm|or)\b/i.test(head);
                const taskId = `doc_${++n}`;
                items.push({ taskId, kind: isQuestion ? "choose" : "approve", title: head.replace(/[.:]$/, ""), why: rest.join(": ") || "", tags: [], options: isQuestion ? undefined : undefined });
                blocks.push({ id: bid("b"), type: "decision", itemId: taskId });
                reasoning[taskId] = isQuestion ? "Read as an open question in the text, so framed as an answer." : "Read as an approval line in the text, so framed as a yes or no.";
                continue;
            }
            const row = line.match(/^\|(.+)\|$/);
            if (row) {
                const cells = row[1].split("|").map((c) => c.trim());
                if (cells.every((c) => /^:?-+:?$/.test(c))) continue;
                const last = blocks[blocks.length - 1];
                if (last?.type === "table") last.rows.push(cells);
                else { flush(); blocks.push({ id: bid("b"), type: "table", title: heading || "Table", columns: cells, rows: [] }); }
                continue;
            }
            buf.push(line);
        }
        flush();
        const title = lines.find((l) => l.trim())?.trim().replace(/^#+\s*/, "") ?? `Pack for ${businessName}`;
        return delay({ title, intro: `Imported from a document. ${items.length} decision${items.length === 1 ? "" : "s"} were found in the text; check each before publishing.`, blocks, items, reasoning }, 1200);
    },

    async reviseItem(item: PackItem, thread: Thread | null): Promise<AgentRevision> {
        const last = [...(thread?.messages ?? [])].reverse().find((m) => m.authorKind === "client" && (m.kind === "message" || m.kind === "suggested_edit"));
        if (last?.suggestedEdit) {
            return delay({ itemId: item.id, understood: `${last.authorName} rewrote the wording directly.`, proposed: last.suggestedEdit.after, questionForClient: null, reasoning: "Their edit keeps the meaning and removes the contested claim. Safe to accept as written." }, 900);
        }
        const comment = (last?.body ?? "").trim();
        const keep = comment.match(/\bkeep (?:the )?([^,.]+)/i)?.[1]?.trim();
        const only = comment.match(/\bonly (?:change|update|rewrite) (?:the )?([^,.]+)/i)?.[1]?.trim();
        const understood = keep || only
            ? `You want us to ${keep ? `keep the ${keep}` : ""}${keep && only ? " and " : ""}${only ? `change only the ${only}` : ""}.`
            : comment ? `You said: "${comment}"` : "You asked for changes without saying which. We need one line on what to change.";
        const base = item.proposed || item.outline?.join(". ") || item.title;
        const proposed = keep || only ? `${base} ${keep ? `The ${keep} stays exactly as it is.` : ""}${only ? ` Only the ${only} changes.` : ""}`.trim() : base;
        return delay({ itemId: item.id, understood, proposed, questionForClient: comment ? null : "What would you like changed?", reasoning: comment ? "Scoped the revision to what the comment named and left everything else untouched." : "No detail in the comment, so this goes back as a question rather than a guess." }, 1000);
    },
};
